import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Star, Archive, Check, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const PAGE_SIZE = 10;

export function DashboardReviews() {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'all' | 'published' | 'archived'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-reviews', tab, currentPage, search],
    queryFn: async () => {
      let query = supabase
        .from('reviews')
        .select('*, profiles!reviews_reviewer_user_id_fkey(full_name, avatar_url)', { count: 'exact' });

      if (tab === 'published') query = query.eq('status', 'published');
      else if (tab === 'archived') query = query.eq('status', 'archived');

      if (search) {
        query = query.or(`title.ilike.%${search}%,text.ilike.%${search}%`);
      }

      // If not superadmin, only show reviews related to user's properties
      if (!hasRole('superadmin')) {
        query = query.or(`agent_user_id.eq.${user?.id}`);
      }

      const from = (currentPage - 1) * PAGE_SIZE;
      query = query.order('created_at', { ascending: false }).range(from, from + PAGE_SIZE - 1);

      const { data: reviews, count, error } = await query;
      if (error) throw error;
      return { reviews: reviews || [], total: count || 0 };
    },
    enabled: !!user,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('reviews').update({ status } as any).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-reviews'] });
      toast.success('Review status updated');
    },
    onError: () => toast.error('Failed to update review'),
  });

  const reviews = data?.reviews || [];
  const totalCount = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search here..."
            className="pl-9 bg-muted/50 border-0 h-9 rounded-lg"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          />
        </div>

        <div className="flex gap-4 text-sm">
          {(['all', 'published', 'archived'] as const).map(t => (
            <button
              key={t}
              className={`capitalize pb-1.5 transition-colors ${tab === t ? 'text-primary border-b-2 border-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => { setTab(t); setCurrentPage(1); }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
        <div className="divide-y divide-border">
          {reviews.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No reviews found</div>
          ) : reviews.map((review: any) => {
            const reviewerName = review.profiles?.full_name || 'Anonymous';
            const rating = review.rating || 0;
            const tags: string[] = review.tags || [];

            return (
              <div key={review.id} className="p-5 flex items-start gap-5">
                <div className="shrink-0">
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-xl font-bold text-muted-foreground">
                    {reviewerName[0]?.toUpperCase() || '?'}
                  </div>
                </div>

                <div className="shrink-0 min-w-[140px]">
                  <div className="text-[11px] text-primary font-semibold">#{review.id?.slice(0, 6)}</div>
                  <h4 className="font-semibold text-sm mt-0.5">{reviewerName}</h4>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {new Date(review.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  {review.title && <div className="text-sm font-medium mb-1">{review.title}</div>}
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {review.text || 'No review text'}
                  </p>
                  {tags.length > 0 && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {tags.map((tag: string, i: number) => (
                        <span key={`${tag}-${i}`} className="text-[10px] font-bold px-3 py-1 rounded-full border text-primary border-primary/20">
                          {tag.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="shrink-0 text-right flex items-center gap-2">
                  <span className="text-lg font-bold">{rating}.0</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-muted'}`} />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2 shrink-0 ml-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs rounded-lg px-4"
                    onClick={() => updateStatus.mutate({ id: review.id, status: 'archived' })}
                    disabled={review.status === 'archived'}
                  >
                    <Archive className="h-3 w-3" /> Archive
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5 text-xs rounded-lg px-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={() => updateStatus.mutate({ id: review.id, status: 'published' })}
                    disabled={review.status === 'published'}
                  >
                    <Check className="h-3 w-3" /> Approve
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between p-4 border-t">
            <span className="text-xs text-muted-foreground">
              Showing {Math.min(PAGE_SIZE, reviews.length)} from {totalCount} data
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="gap-1 text-xs rounded-lg px-3" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
                <ChevronsLeft className="h-3.5 w-3.5" /> Previous
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                <Button
                  key={i}
                  variant={currentPage === i + 1 ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8 text-xs rounded-lg"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button variant="outline" size="sm" className="gap-1 text-xs rounded-lg px-3" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                Next <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
